import { themeColors, themeMode } from '@juvare/common-web-ui/JuvareTheme';
import React from 'react';
/**
 * Altcha challenge widget rendered using a web component and customized for theme and accessibility.
 *
 * @param {AltchaWidgetProps} root0 - Props object.
 * @param {function(string): void} root0.onVerified - Callback executed when Altcha verification succeeds.
 * @param {{[key: string]: string}} [root0.labels] - Optional localization labels passed to the Altcha widget.
 * @param {string} [root0.ariaDescribedBy] - Optional ARIA descriptor ID for accessibility.
 * @param {ChallengeProp} [root0.challengeProp] - Optional props forwarded to the Altcha widget (e.g., challenge config).
 *
 * @returns {React.ReactElement} React element rendering the Altcha widget component.
 */
export const AltchaWidget = ({ onVerified, labels, ariaDescribedBy, challengeProp }) => {
    const altchaRef = React.useRef(null);
    React.useEffect(() => {
        const handleVerified = (event) => {
            const detail = event.detail;
            onVerified(detail.payload);
        };
        const handleAddAriaDescribedBy = () => {
            const element = document.getElementById('altcha_checkbox');
            if (element && ariaDescribedBy) {
                element.setAttribute('aria-describedby', ariaDescribedBy);
            }
        };
        const altchaElement = altchaRef.current;
        altchaElement === null || altchaElement === void 0 ? void 0 : altchaElement.addEventListener('verified', handleVerified);
        altchaElement === null || altchaElement === void 0 ? void 0 : altchaElement.addEventListener('load', handleAddAriaDescribedBy);
        return () => {
            altchaElement === null || altchaElement === void 0 ? void 0 : altchaElement.removeEventListener('verified', handleVerified);
            altchaElement === null || altchaElement === void 0 ? void 0 : altchaElement.removeEventListener('load', handleAddAriaDescribedBy);
        };
    }, [ariaDescribedBy, onVerified]);
    return React.createElement('altcha-widget', Object.assign({ ref: altchaRef, theme: themeMode === 'light' ? 'light' : 'dark', hidelogo: true, hidefooter: true, style: {
            '--altcha-border-width': '1px',
            '--altcha-border-radius': '4px',
            '--altcha-color-base': themeColors.surface.primary,
            '--altcha-color-border': themeColors.border.main,
            '--altcha-color-text': themeColors.text.main,
            '--altcha-color-border-focus': themeColors.border.bold,
            '--altcha-color-error-text': themeColors.accent.red.bold,
            '--altcha-color-footer-bg': themeColors.surface.secondary,
            '--altcha-max-width': '260px',
        }, strings: labels ? JSON.stringify(labels) : undefined }, challengeProp));
};
